/** Serializable interaction progress. World deadlines remain simulation-owned. */
export interface InteractiveAttempt {
  kind: 'timing' | 'tempo' | 'stretch' | 'hold';
  elapsed: number;
  pull: number;
  hits: number;
  misses: number;
  early: number;
  late: number;
  scoreSum: number;
  center: number;
  claimed: (null | 'hit' | 'miss')[];
  completed: boolean;
  /** Frozen minigame settings prevent purchasing a retry with easier windows. */
  parameters: Record<string, number>;
  milk?: { pasture?: boolean; day: number; feedType: 'basic' | 'quality'; careLevel: number; effortLevel: number };
}

export function newAttempt(kind: InteractiveAttempt['kind']): InteractiveAttempt {
  return { kind, elapsed: 0, pull: 0, hits: 0, misses: 0, early: 0, late: 0,
    scoreSum: 0, center: 0.5, claimed: [], completed: false, parameters: {} };
}

export function normalizeAttempts(value: unknown): Record<string, InteractiveAttempt> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const result: Record<string, InteractiveAttempt> = {};
  for (const [key, raw] of Object.entries(value).slice(-20)) {
    if (!raw || typeof raw !== 'object') continue;
    const a = raw as InteractiveAttempt;
    if (!['timing', 'tempo', 'stretch', 'hold'].includes(a.kind)) continue;
    const clean = newAttempt(a.kind);
    for (const k of ['elapsed','pull','hits','misses','early','late','scoreSum','center'] as const) {
      clean[k] = Number.isFinite(a[k]) ? Math.max(0, Math.min(k === 'elapsed' ? 1e9 : k === 'center' ? 1 : 100, a[k])) : clean[k];
    }
    clean.claimed = Array.isArray(a.claimed) ? a.claimed.slice(0, 100).map(v => v === 'hit' || v === 'miss' ? v : null) : [];
    clean.completed = a.completed === true;
    clean.parameters = Object.fromEntries(Object.entries(a.parameters ?? {}).filter(([,v]) => Number.isFinite(v)));
    if (a.milk && Number.isFinite(a.milk.day) && ['basic','quality'].includes(a.milk.feedType)) {
      clean.milk = { pasture: typeof a.milk.pasture === 'boolean' ? a.milk.pasture : undefined, day: a.milk.day, feedType: a.milk.feedType,
        careLevel: Number.isFinite(a.milk.careLevel) ? Math.max(0,Math.min(15,a.milk.careLevel)) : 0,
        effortLevel: Number.isFinite(a.milk.effortLevel) ? Math.max(0,Math.min(15,a.milk.effortLevel)) : 0 };
    }
    result[key] = clean;
  }
  return result;
}
